import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Download, ExternalLink, RefreshCw } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import AutoSEOImportPanel from "@/components/admin/AutoSEOImportPanel";
import { adminApi } from "@/lib/adminApi";

/**
 * Editorial Dashboard — single source of truth for every article
 * (AutoSEO + manual). Two strictly separated operational lists:
 * PUBLISHED (numerically ascending by ART number) and DRAFTS (newest
 * first). Server-side pagination, combinable filters, Needs Attention
 * triage and CSV export. Strictly read-only.
 */

const CATEGORIES = [
  "marcus-aurelius", "epictetus", "seneca",
  "stoic-practice", "virtues", "modern-life", "virtue-guide",
];

const ATTENTION_META = {
  missing_en: { label: "Missing EN", style: "border-red-400/50 text-red-300" },
  missing_nl: { label: "Missing NL", style: "border-red-400/50 text-red-300" },
  awaiting_en_approval: { label: "Awaiting EN approval", style: "border-gold/50 text-gold" },
  awaiting_nl_approval: { label: "Awaiting NL approval", style: "border-gold/50 text-gold" },
  missing_seo: { label: "Missing SEO", style: "border-amber-400/50 text-amber-300" },
  missing_category: { label: "Missing category", style: "border-amber-400/50 text-amber-300" },
  old_draft: { label: "Old draft (>14d)", style: "border-orange-400/50 text-orange-300" },
  ready_to_publish: { label: "Ready to publish", style: "border-emerald-400/50 text-emerald-300" },
};

const STATUS_STYLE = {
  published: "border-emerald-400/50 text-emerald-300",
  draft: "border-sky-400/50 text-sky-300",
};

// Per-tab default sort: Published follows the ART sequence numerically
// ascending; Drafts follow arrival date ascending (oldest first).
const TAB_SORT = {
  published: { sort_by: "reference", sort_dir: "asc" },
  drafts: { sort_by: "created_at", sort_dir: "asc" },
};

const DEFAULT_FILTERS = { q: "", source: "", category: "", attention: "" };

const langCell = (s) => (
  <span className={s === "approved" ? "text-emerald-300" : s === "awaiting approval" ? "text-gold" : "text-bone/35"}>
    {s === "awaiting approval" ? "awaiting" : s || "—"}
  </span>
);

function StatCard({ label, value, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left px-4 py-3 border transition-colors ${active ? "border-gold bg-gold/10" : "border-white/10 bg-white/[0.02] hover:border-gold/40"}`}>
      <p className="text-2xl font-serif text-bone leading-none">{value ?? "—"}</p>
      <p className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-bone/50">{label}</p>
    </button>
  );
}

export default function AdminEditorialDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = tabParam === "autoseo" ? "autoseo" : tabParam === "drafts" ? "drafts" : "published";

  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...(TAB_SORT[tab] || TAB_SORT.published) });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);

  // The tab IS the status filter: the two operational lists never mix.
  const listStatus = tab === "drafts" ? "draft" : "published";

  const loadStats = useCallback(async () => {
    try { setStats(await adminApi.editorialStats()); }
    catch (e) { setErr(e.message); }
  }, []);

  const loadArticles = useCallback(async () => {
    if (tab === "autoseo") return;
    setLoading(true); setErr("");
    try {
      setData(await adminApi.editorialArticles({ ...filters, status: listStatus, page, limit }));
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filters, listStatus, page, limit, tab]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadArticles(); }, [loadArticles]);

  const setFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };

  const setTab = (key, extraFilters = {}) => {
    setSearchParams(key === "published" ? {} : { tab: key });
    if (key !== "autoseo") {
      setFilters({ ...DEFAULT_FILTERS, ...TAB_SORT[key], ...extraFilters });
      setSearchInput("");
      setPage(1);
    }
  };

  const onSearch = (v) => {
    setSearchInput(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setFilter({ q: v }), 350);
  };

  const resetFilters = () => {
    setSearchInput("");
    setFilters({ ...DEFAULT_FILTERS, ...(TAB_SORT[tab] || TAB_SORT.published) });
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true); setErr("");
    try {
      const blob = await adminApi.editorialExport({ ...filters, status: listStatus });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stoicos-editorial-${listStatus}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); }
    finally { setExporting(false); }
  };

  const totals = stats?.totals || {};
  const att = stats?.attention || {};
  const imports = stats?.imports || {};
  const rows = data?.rows || [];

  const attentionChips = [
    ["missing_nl", att.missing_nl], ["missing_en", att.missing_en],
    ["awaiting_en_approval", att.awaiting_en_approval], ["awaiting_nl_approval", att.awaiting_nl_approval],
    ["missing_seo", att.missing_seo], ["missing_category", att.missing_category],
    ["old_draft", att.old_draft],
  ].filter(([, n]) => n > 0);

  return (
    <AdminShell title="Editorial Dashboard">
      <p className="text-sm text-bone/60 -mt-6 mb-6 max-w-3xl">
        Central control center for every article — AutoSEO and manual. Read-only: nothing here
        publishes, deletes or resends anything. Publishing stays a manual EN + NL approval flow.
      </p>

      {/* Tabs — Published and Drafts are strictly separated lists */}
      <div className="flex gap-2 mb-8" data-testid="editorial-tabs">
        {[
          ["published", `Published${totals.published != null ? ` (${totals.published})` : ""}`],
          ["drafts", `Drafts${totals.drafts != null ? ` (${totals.drafts})` : ""}`],
          ["autoseo", "AutoSEO Imports"],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            data-testid={`editorial-tab-${key}`}
            className={`px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase border transition-colors ${
              tab === key ? "border-gold text-gold bg-gold/10" : "border-white/15 text-bone/60 hover:border-gold/40"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "autoseo" ? <AutoSEOImportPanel /> : (
        <>
          {/* Summary counters */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="editorial-stats">
            <StatCard label="Total articles" value={totals.total} testid="stat-total"
              active={false} onClick={resetFilters} />
            <StatCard label="Published" value={totals.published} testid="stat-published"
              active={tab === "published"} onClick={() => setTab("published")} />
            <StatCard label="Drafts" value={totals.drafts} testid="stat-drafts"
              active={tab === "drafts" && !filters.attention} onClick={() => setTab("drafts")} />
            <StatCard label="Awaiting EN approval" value={att.awaiting_en_approval} testid="stat-awaiting-en"
              active={tab === "drafts" && filters.attention === "awaiting_en_approval"}
              onClick={() => setTab("drafts", { attention: "awaiting_en_approval" })} />
            <StatCard label="Awaiting NL approval" value={att.awaiting_nl_approval} testid="stat-awaiting-nl"
              active={tab === "drafts" && filters.attention === "awaiting_nl_approval"}
              onClick={() => setTab("drafts", { attention: "awaiting_nl_approval" })} />
            <StatCard label="Ready to publish" value={att.ready_to_publish} testid="stat-ready"
              active={tab === "drafts" && filters.attention === "ready_to_publish"}
              onClick={() => setTab("drafts", { attention: "ready_to_publish" })} />
          </div>

          {/* AutoSEO ops strip */}
          <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="editorial-ops-strip">
            <span className={`text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border ${imports.paused ? "border-amber-400/50 text-amber-300" : "border-emerald-400/40 text-emerald-300"}`}>
              {imports.paused ? "AutoSEO paused" : "AutoSEO active"}
            </span>
            <span className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-white/15 text-bone/60">
              Week budget · {imports.weekly_created ?? 0} / {imports.weekly_limit ?? 7}
            </span>
            <button onClick={() => setTab("autoseo")} data-testid="ops-imports-link"
              className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-white/15 text-bone/60 hover:border-gold/40 hover:text-gold">
              {imports.failed ?? 0} failed · {imports.skipped ?? 0} skipped · {imports.duplicates ?? 0} duplicates → import log
            </button>
            <button onClick={() => { loadStats(); loadArticles(); }} data-testid="editorial-refresh"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/40 px-4 py-2 hover:bg-gold/10 ml-auto">
              <RefreshCw size={12} strokeWidth={1.5} /> Refresh
            </button>
          </div>

          {/* Needs attention */}
          <div className="border border-amber-400/25 bg-amber-400/[0.03] p-4 sm:p-5 mb-8" data-testid="needs-attention-panel">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={14} strokeWidth={1.5} className="text-amber-300" />
              <h2 className="text-[11px] tracking-[0.28em] uppercase text-amber-300">Needs Attention</h2>
            </div>
            {attentionChips.length === 0 ? (
              <p className="text-sm text-bone/50" data-testid="needs-attention-empty">Nothing needs attention right now.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {attentionChips.map(([key, n]) => (
                  <button key={key} onClick={() => setFilter({ attention: filters.attention === key ? "" : key })}
                    data-testid={`attention-chip-${key}`}
                    className={`px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase border transition-colors ${
                      filters.attention === key ? "border-gold text-gold bg-gold/10" : `${ATTENTION_META[key].style} hover:bg-white/5`
                    }`}>
                    {ATTENTION_META[key].label} · {n}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="editorial-filters">
            <input type="text" value={searchInput} onChange={(e) => onSearch(e.target.value)}
              placeholder="Search ART-0011, title, slug, AutoSEO ID…"
              data-testid="editorial-search"
              className="w-full sm:w-72 bg-white/[0.03] border border-white/15 focus:border-gold/60 outline-none px-3 py-2.5 text-sm text-bone placeholder:text-bone/30" />
            <select value={filters.source} onChange={(e) => setFilter({ source: e.target.value })} data-testid="filter-source"
              className="bg-ink border border-white/15 text-bone/70 text-[12px] px-3 py-2.5 outline-none focus:border-gold/60">
              <option value="">All sources</option>
              <option value="autoseo">AutoSEO</option>
              <option value="manual">Manual</option>
            </select>
            <select value={filters.category} onChange={(e) => setFilter({ category: e.target.value })} data-testid="filter-category"
              className="bg-ink border border-white/15 text-bone/70 text-[12px] px-3 py-2.5 outline-none focus:border-gold/60">
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.attention} onChange={(e) => setFilter({ attention: e.target.value })} data-testid="filter-attention"
              className="bg-ink border border-white/15 text-bone/70 text-[12px] px-3 py-2.5 outline-none focus:border-gold/60">
              <option value="">All attention states</option>
              {Object.entries(ATTENTION_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            <select value={`${filters.sort_by}:${filters.sort_dir}`}
              onChange={(e) => { const [sort_by, sort_dir] = e.target.value.split(":"); setFilter({ sort_by, sort_dir }); }}
              data-testid="filter-sort"
              className="bg-ink border border-white/15 text-bone/70 text-[12px] px-3 py-2.5 outline-none focus:border-gold/60">
              {tab === "published" ? (
                <>
                  <option value="reference:asc">Ref ART ↑</option>
                  <option value="reference:desc">Ref ART ↓</option>
                  <option value="published_at:desc">Recently published</option>
                  <option value="published_at:asc">First published</option>
                  <option value="updated_at:desc">Newest updated</option>
                  <option value="title:asc">Title A–Z</option>
                </>
              ) : (
                <>
                  <option value="created_at:asc">Arrival date ↑ (oldest first)</option>
                  <option value="created_at:desc">Arrival date ↓ (newest first)</option>
                  <option value="updated_at:desc">Newest updated</option>
                  <option value="updated_at:asc">Oldest updated</option>
                  <option value="title:asc">Title A–Z</option>
                </>
              )}
            </select>
            <button onClick={resetFilters} data-testid="editorial-reset"
              className="text-[11px] tracking-[0.2em] uppercase text-bone/50 border border-white/15 px-4 py-2.5 hover:border-gold/40 hover:text-gold">
              Reset
            </button>
            <button onClick={exportCsv} disabled={exporting} data-testid="editorial-export-csv"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-gold border border-gold/40 px-4 py-2.5 hover:bg-gold/10 disabled:opacity-40 sm:ml-auto">
              <Download size={13} strokeWidth={1.5} /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>

          {/* Table */}
          <div className="border border-gold/15 bg-white/[0.02] overflow-x-auto" data-testid="editorial-table">
            <table className="w-full text-left min-w-[1100px]">
              <thead>
                <tr className="border-b border-white/10">
                  {["Ref", "Title", "Status", "Source", "EN", "NL", "SEO", "Category", "Days open", "Attention", "Updated", ""].map((h, i) => (
                    <th key={i} className="px-3 py-3 text-[10px] tracking-[0.22em] uppercase text-bone/50 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={12} className="px-4 py-6 text-sm text-bone/50">Loading…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={12} className="px-4 py-6 text-sm text-bone/50" data-testid="editorial-empty">No articles match these filters.</td></tr>
                )}
                {!loading && rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-white/5 last:border-b-0 align-top" data-testid={`editorial-row-${i}`}>
                    <td className="px-3 py-2.5 text-[12px] font-mono text-gold whitespace-nowrap">{r.article_reference || "—"}</td>
                    <td className="px-3 py-2.5 text-[13px] text-bone max-w-[280px]">
                      <span className="block truncate" title={r.title_en || r.title_nl}>{r.title_en || r.title_nl || "—"}</span>
                      <span className="block text-[11px] font-mono text-bone/35 truncate">{r.slug}</span>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span data-testid={`editorial-status-${i}`} className={`text-[10px] tracking-[0.16em] uppercase px-2 py-0.5 border ${STATUS_STYLE[r.status] || "border-white/20 text-bone/50"}`}>
                        {r.status}
                      </span>
                      {r.ready_to_publish && (
                        <span className="block mt-1 text-[10px] tracking-[0.14em] uppercase text-emerald-300">ready</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide text-bone/50 whitespace-nowrap">{r.source}</td>
                    <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide whitespace-nowrap">{langCell(r.en_status)}</td>
                    <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide whitespace-nowrap">{langCell(r.nl_status)}</td>
                    <td className="px-3 py-2.5 text-[12px] whitespace-nowrap">
                      <span className={r.has_seo ? "text-emerald-300" : "text-amber-300"}>{r.has_seo ? "✓" : "missing"}</span>
                    </td>
                    <td className="px-3 py-2.5 text-[12px] text-bone/60 whitespace-nowrap">{r.category || "—"}</td>
                    <td className="px-3 py-2.5 text-[12px] whitespace-nowrap">
                      {r.days_open == null ? <span className="text-bone/30">—</span> : (
                        <span className={r.days_open > 14 ? "text-orange-300" : "text-bone/60"}>{r.days_open}d</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      <div className="flex flex-wrap gap-1">
                        {(r.attention || []).map((a) => (
                          <span key={a} className={`text-[9px] tracking-[0.1em] uppercase px-1.5 py-0.5 border ${ATTENTION_META[a]?.style || "border-white/20 text-bone/50"}`}>
                            {ATTENTION_META[a]?.label || a}
                          </span>
                        ))}
                        {(r.attention || []).length === 0 && <span className="text-[11px] text-bone/30">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[11px] font-mono text-bone/50 whitespace-nowrap">{String(r.updated_at).slice(0, 10)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Link to={`/admin/journal?edit=${encodeURIComponent(r.slug)}`} data-testid={`editorial-open-${i}`}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-gold hover:text-bone">
                        Open <ExternalLink size={11} strokeWidth={1.5} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="editorial-pagination">
            <p className="text-[12px] text-bone/50">
              {data ? `${data.total} ${listStatus} article${data.total === 1 ? "" : "s"} · page ${data.page} of ${data.pages}` : "…"}
            </p>
            <div className="flex items-center gap-2 ml-auto">
              <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} data-testid="editorial-page-size"
                className="bg-ink border border-white/15 text-bone/70 text-[12px] px-2 py-1.5 outline-none">
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="editorial-prev-page"
                className="px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase border border-white/15 text-bone/60 hover:border-gold/40 disabled:opacity-30">
                Prev
              </button>
              <button disabled={!data || page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="editorial-next-page"
                className="px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase border border-white/15 text-bone/60 hover:border-gold/40 disabled:opacity-30">
                Next
              </button>
            </div>
          </div>

          {err && <p className="mt-4 text-sm text-red-400" data-testid="editorial-error">{err}</p>}
        </>
      )}
    </AdminShell>
  );
}
